import type { ParcelPropertiesInfo } from "../types";
import { decodeParcelString, encodeParcelStringColumn, PARCEL_COLUMNS, type ParcelColumns } from "./columns";

/**
 * The visible-parcels list as it crosses the worker boundary: twelve columns of packed UTF-8 rather than
 * an array of objects, so the transfer costs nothing and no row is built until something reads it.
 *
 * A refresh must always produce a new instance — the store's subscribers compare by reference.
 */
class VisibleParcels {
	public readonly length: number;
	private readonly columns: ParcelColumns;
	private readonly decoder = new TextDecoder();

	constructor({ columns, length }: { columns: ParcelColumns; length: number }) {
		this.columns = columns;
		this.length = length;
	}

	/** The parcel id alone, for the hover and favourite lookups that never need the rest of the row. */
	public getId(index: number): string {
		return decodeParcelString(this.columns.id, index, this.decoder);
	}

	/** Builds one row on demand. Callers hold the index, not the object, so nothing materializes the list. */
	public getRow(index: number): ParcelPropertiesInfo {
		const row = {} as ParcelPropertiesInfo;

		for (const column of PARCEL_COLUMNS) {
			row[column] = decodeParcelString(this.columns[column], index, this.decoder);
		}

		return row;
	}

	/** Escape hatch for the favourites tab and for tests, which hold plain rows rather than a worker payload. */
	public static fromRows(rows: ReadonlyArray<ParcelPropertiesInfo>): VisibleParcels {
		const columns = {} as ParcelColumns;

		for (const column of PARCEL_COLUMNS) {
			columns[column] = encodeParcelStringColumn(rows.map((row) => row[column]));
		}

		return new VisibleParcels({ columns, length: rows.length });
	}

	public static empty(): VisibleParcels {
		return VisibleParcels.fromRows([]);
	}
}

export { VisibleParcels };
